import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import type { TFunction } from 'i18next';
import { useUIStore } from '@/store/uiStore';
import Sidebar from '@/components/common/Sidebar';
import TrialBanner from '@/components/common/TrialBanner';
import SaleRegisterSection from '@/components/reports/SaleRegisterSection';
import SaleSummarySection from '@/components/reports/SaleSummarySection';
import PartySaleSummarySection from '@/components/reports/PartySaleSummarySection';
import RecoveryReceiptsSection from '@/components/reports/RecoveryReceiptsSection';
import SaleActivityInvoiceSection from '@/components/reports/SaleActivityInvoiceSection';
import SaleActivityPartySection from '@/components/reports/SaleActivityPartySection';
import CustomerLedgerReportSection from '@/components/reports/CustomerLedgerReportSection';
import ItemsSaleSummarySection from '@/components/reports/ItemsSaleSummarySection';
import ItemsSaleAnalysisSection from '@/components/reports/ItemsSaleAnalysisSection';
import ServicesAnalysisSection from '@/components/reports/ServicesAnalysisSection';
import SupplierLedgerReportSection from '@/components/reports/SupplierLedgerReportSection';
import ServiceJobsReportSection from '@/components/reports/ServiceJobsReportSection';
import PurchaseSummaryPage from '@/features/purchase_reports/purchase-summary/page';
import SupplierPurchaseSummaryPage from '@/features/purchase_reports/supplier-summary/page';
import PurchaseActivityInvoicePage from '@/features/purchase_reports/activity-invoice/page';
import PurchaseActivitySupplierPage from '@/features/purchase_reports/activity-supplier/page';
import ItemPurchaseAnalysisPage from '@/features/purchase_reports/item-analysis/page';
import PurchasePaymentReportPage from '@/features/purchase_reports/payments/page';
import OutstandingPayablesPage from '@/features/purchase_reports/outstanding/page';
import PurchaseRegisterPage from '@/features/purchase_reports/register/page';
import DailyCashLedgerSection from '@/components/reports/DailyCashLedgerSection';
import CashSummarySection from '@/components/reports/CashSummarySection';
import XReportSection from '@/components/reports/XReportSection';
import ZReportSection from '@/components/reports/ZReportSection';
import ZDayReportSection from '@/components/reports/ZDayReportSection';
import ShiftPerformanceSection from '@/components/reports/ShiftPerformanceSection';
import CommissionReportSection from '@/components/reports/CommissionReportSection';
import PromotionReportSection from '@/components/reports/PromotionReportSection';
import ReturnedInvoicesSection from '@/components/reports/ReturnedInvoicesSection';
import { Menu } from 'lucide-react';
import { useSyncStore } from '@/store/syncStore';
import { isLocalFirstMode } from '@/offline';
import LocalOfflineReports from '@/components/offline/LocalOfflineReports';
import SyncModeBanner from '@/components/pos/SyncModeBanner';

import ProfitLossReportSection from '@/components/reports/ProfitLossReportSection';
import ShopStatusReportSection from '@/components/reports/ShopStatusReportSection';
import CashFlowDashboardSection from '@/components/reports/CashFlowDashboardSection';
import SalaryReportSection from '@/components/reports/SalaryReportSection';
// import VatReturnSummarySection from '@/components/reports/VatReturnSummarySection';

export type ReportsSection = 'sales' | 'purchases' | 'general' | 'analysis';

export type SalesReportKind =
  | 'register'
  | 'summary'
  | 'party-summary'
  | 'recovery'
  | 'activity-invoice'
  | 'activity-party'
  | 'items-summary'
  | 'items-analysis'
  | 'services-analysis'
  | 'service-jobs'
  | 'x-report'
  | 'z-report'
  | 'z-day'
  | 'shift-performance'
  | 'commission'
  | 'returned-invoices'
  | 'promotions'
  | 'customer-ledger';

export type PurchasesReportKind =
  | 'register'
  | 'summary'
  | 'supplier-summary'
  | 'activity-invoice'
  | 'activity-supplier'
  | 'item-analysis'
  | 'payments'
  | 'outstanding'
  | 'supplier-ledger';

export type GeneralReportKind = 'daily-cash-ledger' | 'salary-report' | 'cash-summary';
export type AnalysisReportKind =
  | 'profit-loss'
  | 'shop-status'
  | 'cash-flow-dashboard';
  // | 'vat-summary';

export type ReportKind = SalesReportKind | PurchasesReportKind | GeneralReportKind | AnalysisReportKind;

interface Props {
  section: ReportsSection;
  report: ReportKind;
}



const getSalesReportMeta = (t: TFunction): Record<SalesReportKind, { title: string; subtitle: string }> => ({
  register: {
    title: t('nav.saleRegister'),
    subtitle: t('reports.saleRegisterSub'),
  },
  summary: {
    title: t('nav.saleSummary', 'Sale Summary'),
    subtitle: t('reports.saleSummarySub'),
  },
  'party-summary': {
    title: t('nav.customerSaleSummary'),
    subtitle: t('reports.partySaleSub'),
  },
  recovery: {
    title: t('nav.recoveryReceipts'),
    subtitle: t('reports.recoverySub'),
  },
  'activity-invoice': {
    title: t('nav.saleActivityInvoice', 'Sale Activity — Invoice Wise'),
    subtitle: t('reports.saleActivityInvoiceSub'),
  },
  'activity-party': {
    title: t('nav.saleActivityCustomer'),
    subtitle: t('reports.saleActivityPartySub'),
  },
  'items-summary': {
    title: t('nav.itemsSaleSummary'),
    subtitle: t('reports.itemsSaleSummarySub'),
  },
  'items-analysis': {
    title: t('nav.itemsSaleAnalysis'),
    subtitle: t('reports.itemsSaleAnalysisSub'),
  },
  'services-analysis': {
    title: t('nav.servicesAnalysis'),
    subtitle: t('reports.servicesAnalysisSub'),
  },
  'service-jobs': {
    title: t('nav.serviceJobsReport', 'Service Jobs Report'),
    subtitle: t('reports.serviceJobsReportSub', 'Assigned services, incomplete jobs, and staff performance'),
  },
  'x-report': {
    title: t('nav.xReport', 'X Report'),
    subtitle: t('reports.xReportSub', 'Mid-shift reading — does not end the shift'),
  },
  'z-report': {
    title: t('nav.zReport', 'Z Report'),
    subtitle: t('reports.zReportSub', 'Closed shift finalize report (reprint)'),
  },
  'z-day': {
    title: t('nav.zDayReport', 'Z Day Report'),
    subtitle: t('reports.zDayReportSub', 'Store rollup of all closed shifts for a date'),
  },
  'shift-performance': {
    title: 'Shift Performance',
    subtitle: 'Cashier shift analysis by date range',
  },
  commission: {
    title: t('commission.report.title'),
    subtitle: t('commission.report.subtitle'),
  },
  'returned-invoices': {
    title: t('nav.returnedInvoices', 'Returned Invoices'),
    subtitle: t('reports.returnedInvoicesSub', 'List of all returned invoices and items'),
  },
  promotions: {
    title: t('promotions.report.title'),
    subtitle: t('promotions.report.subtitle'),
  },
  'customer-ledger': {
    title: 'Customer Ledger',
    subtitle: 'Customer debit/credit ledger',
  },
});

const getPurchasesReportMeta = (t: TFunction): Record<PurchasesReportKind, { title: string; subtitle: string }> => ({
  register: {
    title: t('nav.purchaseRegister'),
    subtitle: t('reports.purchaseRegisterSub'),
  },
  summary: {
    title: t('nav.purchaseSummary'),
    subtitle: t('reports.purchaseSummarySub'),
  },
  'supplier-summary': {
    title: t('nav.supplierPurchaseSummary'),
    subtitle: t('reports.supplierPurchaseSub'),
  },
  'activity-invoice': {
    title: t('nav.purchaseActivityInvoice'),
    subtitle: t('reports.purchaseActivityInvoiceSub'),
  },
  'activity-supplier': {
    title: t('nav.purchaseActivitySupplier'),
    subtitle: t('reports.purchaseActivitySupplierSub'),
  },
  'item-analysis': {
    title: t('nav.itemPurchaseAnalysis'),
    subtitle: t('reports.itemPurchaseSub'),
  },
  payments: {
    title: t('nav.purchasePaymentReport'),
    subtitle: t('reports.purchasePaymentSub'),
  },
  outstanding: {
    title: t('nav.outstandingPayables'),
    subtitle: t('reports.outstandingPayablesSub'),
  },
  'supplier-ledger': {
    title: 'Supplier Ledger',
    subtitle: 'Supplier debit/credit ledger',
  },
});

const getGeneralReportMeta = (t: TFunction): Record<GeneralReportKind, { title: string; subtitle: string }> => ({
  'daily-cash-ledger': {
    title: t('nav.cashLedger', 'Cash Ledger'),
    subtitle: t('reports.cashLedgerSub', 'Cash book of incoming and outgoing money'),
  },
  'cash-summary': {
    title: 'Cash Summary',
    subtitle: 'Admin overview of Cash in Safe (Drops, Deposits, and Withdrawals)',
  },
  'salary-report': {
    title: 'Salary Report',
    subtitle: 'Track fixed salaries and salary expenses',
  },
});

const getAnalysisReportMeta = (t: TFunction): Record<AnalysisReportKind, { title: string; subtitle: string }> => ({
  'profit-loss': {
    title: t('nav.profitLoss', 'Profit vs Loss'),
    subtitle: t('reports.profitLossSub', 'Profit and loss statement based on actual item costs and expenses'),
  },
  'shop-status': {
    title: t('nav.shopStatus', 'Shop Status'),
    subtitle: t('reports.shopStatusSub', 'Analyze product performance based on sales volume'),
  },
  'cash-flow-dashboard': {
    title: t('nav.cashFlowDashboard', 'Cash Flow Dashboard'),
    subtitle: t('reports.cashFlowDashboardSub', 'Monitor your shop\'s cash movement'),
  },
  // 'vat-summary': {
  //   title: t('nav.vatSummary', 'VAT Return Summary'),
  //   subtitle: t('reports.vatSummarySub', 'Tax and VAT report summary for your business'),
  // },
});

function getReportMeta(t: TFunction, section: ReportsSection, report: ReportKind) {
  if (section === 'sales') {
    return getSalesReportMeta(t)[report as SalesReportKind];
  }
  if (section === 'general') {
    return getGeneralReportMeta(t)[report as GeneralReportKind];
  }
  if (section === 'analysis') {
    return getAnalysisReportMeta(t)[report as AnalysisReportKind];
  }
  return getPurchasesReportMeta(t)[report as PurchasesReportKind];
}

export default function ReportsPage({ section, report }: Props) {
  const { t } = useTranslation();
  const { toggleSidebar } = useUIStore();
  const reportMeta = getReportMeta(t, section, report);
  const localMode = isLocalFirstMode(useSyncStore((s) => s.posSyncMode));

  return (
    <div className="flex h-screen bg-gray-50">
      <Sidebar />
      <div className="flex-1 overflow-auto">
        <SyncModeBanner />
        <TrialBanner />
        <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="flex items-center gap-3 min-w-0">
              <button
                onClick={toggleSidebar}
                className="lg:hidden p-2 -ml-2 text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg shrink-0"
              >
                <Menu className="w-5 h-5" />
              </button>
              <div className="min-w-0">
                <p className="text-xs font-medium uppercase tracking-wide text-gray-400 mb-1">
                  <Link to="/reports" className="hover:text-gray-600 transition-colors inline-flex items-center gap-1">
                    ← Back to Reports
                  </Link>
                </p>
                <h1 className="text-xl sm:text-2xl font-bold text-gray-900 truncate">{reportMeta.title}</h1>
                <p className="text-gray-500 text-sm">{reportMeta.subtitle}</p>
              </div>
            </div>
          </div>

          {localMode ? (
            <LocalOfflineReports />
          ) : (
            <>
              {section === 'sales' && report === 'register' && <SaleRegisterSection />}
              {section === 'sales' && report === 'summary' && <SaleSummarySection />}
              {section === 'sales' && report === 'party-summary' && <PartySaleSummarySection />}
              {section === 'sales' && report === 'recovery' && <RecoveryReceiptsSection />}
              {section === 'sales' && report === 'activity-invoice' && <SaleActivityInvoiceSection />}
              {section === 'sales' && report === 'activity-party' && <SaleActivityPartySection />}
              {section === 'sales' && report === 'customer-ledger' && <CustomerLedgerReportSection />}
              {section === 'sales' && report === 'items-summary' && <ItemsSaleSummarySection />}
              {section === 'sales' && report === 'items-analysis' && <ItemsSaleAnalysisSection />}
              {section === 'sales' && report === 'services-analysis' && <ServicesAnalysisSection />}
              {section === 'sales' && report === 'service-jobs' && <ServiceJobsReportSection />}
              {section === 'sales' && report === 'x-report' && <XReportSection />}
              {section === 'sales' && report === 'z-report' && <ZReportSection />}
              {section === 'sales' && report === 'z-day' && <ZDayReportSection />}
              {section === 'sales' && report === 'shift-performance' && <ShiftPerformanceSection />}
              {section === 'sales' && report === 'commission' && <CommissionReportSection />}
              {section === 'sales' && report === 'promotions' && <PromotionReportSection />}
              {section === 'sales' && report === 'returned-invoices' && <ReturnedInvoicesSection />}
              {section === 'purchases' && report === 'register' && <PurchaseRegisterPage />}
              {section === 'purchases' && report === 'summary' && <PurchaseSummaryPage />}
              {section === 'purchases' && report === 'supplier-summary' && <SupplierPurchaseSummaryPage />}
              {section === 'purchases' && report === 'activity-invoice' && <PurchaseActivityInvoicePage />}
              {section === 'purchases' && report === 'activity-supplier' && <PurchaseActivitySupplierPage />}
              {section === 'purchases' && report === 'item-analysis' && <ItemPurchaseAnalysisPage />}
              {section === 'purchases' && report === 'payments' && <PurchasePaymentReportPage />}
              {section === 'purchases' && report === 'outstanding' && <OutstandingPayablesPage />}
              {section === 'purchases' && report === 'supplier-ledger' && <SupplierLedgerReportSection />}
              {section === 'general' && report === 'daily-cash-ledger' && <DailyCashLedgerSection />}
              {section === 'general' && report === 'cash-summary' && <CashSummarySection />}
              {section === 'general' && report === 'salary-report' && <SalaryReportSection />}
              {section === 'analysis' && report === 'profit-loss' && <ProfitLossReportSection />}
              {section === 'analysis' && report === 'shop-status' && <ShopStatusReportSection />}
              {section === 'analysis' && report === 'cash-flow-dashboard' && <CashFlowDashboardSection />}
              {/* {section === 'analysis' && report === 'vat-summary' && <VatReturnSummarySection />} */}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
